import {
    AppleAppStorePhasedReleaseState,
    AppleAppStoreReleaseType,
    AppStoreReviewSubmissionItemType,
    AutomationType,
    ChecklistItemPlacement,
    ChecklistItemBlockableEvent,
    ChecklistItemStatusType,
    ChecklistAttachment,
    ChecklistItemType,
    GooglePlayStoreState,
    HuaweiPhasedReleaseState,
    RolloutAction,
    RunwayModelsResponse,
    UserGroupId,
    WorkflowType,
    RegressionStatus,
    RelativeTimeTrigger,
    AppPlatform,
} from '@/types';
import { UpdatePlayAppStoreReleaseDefaults } from './appSettings';
import { LocalizationUpdateRequest } from './release';
import { Nullable } from '@/typescriptHelpers';

export enum StepRunUpdatable {
    // Beta testing
    EndBetaSoakPeriod = 'END_BETA_SOAK_PERIOD',
    AssignBetaGroupsToBuild = 'ASSIGN_BETA_GROUPS_TO_BUILD',

    CreateOrUpdateLocalizationNotesForBuild = 'CREATE_OR_UPDATE_LOCALIZATION_NOTES_FOR_BUILD',
    SubmitBetaBuildToReview = 'SUBMIT_BETA_BUILD_TO_REVIEW',
    AddIndividualTesterToBuild = 'ADD_INDIVIDUAL_TESTER_TO_BUILD',
    PromoteBuildsToTestingTrack = 'RELEASE_PROMOTE_BUILDS_TO_TESTING_TRACK',
    // Checklist Items
    UpdateChecklistItemComment = 'UPDATE_CHECKLIST_ITEM_COMMENT',
    UpdateChecklistItemStatus = 'UPDATE_CHECKLIST_ITEM_STATUS',
    PingChecklistItem = 'PING_CHECKLIST_ITEM',
    PingPendingChecklistItems = 'PING_PENDING_CHECKLIST_ITEMS',
    // Checklist Items one-offs
    CreateStepRunChecklistItem = 'CREATE_STEP_RUN_CHECKLIST_ITEM',
    UpdateStepRunChecklistItem = 'UPDATE_STEP_RUN_CHECKLIST_ITEM',
    DeleteStepRunChecklistItem = 'DELETE_STEP_RUN_CHECKLIST_ITEM',

    // App Store
    UpdateSelectedBuildsInAppStore = 'UPDATE_SELECTED_BUILDS_IN_APP_STORE',
    SubmitAppForReview = 'SUBMIT_APP_FOR_REVIEW',
    UpdateAppStoreSettings = 'UPDATE_APP_STORE_SETTINGS',
    UpdateReviewDetails = 'UPDATE_REVIEW_DETAILS',
    UploadReviewAttachment = 'UPLOAD_REVIEW_ATTACHMENT',
    DeleteReviewAttachment = 'DELETE_REVIEW_ATTACHMENT',

    RejectAppleSubmission = 'REJECT_APPLE_SUBMISSION',
    ReleaseAppleVersion = 'RELEASE_APPLE_VERSION',
    RejectVersion = 'REJECT_VERSION',
    KickoffRelease = 'KICKOFF_RELEASE',

    UpdateApplePhasedRelease = 'UPDATE_APPLE_PHASED_RELEASE',
    UpdateGooglePhasedRelease = 'UPDATE_GOOGLE_PHASED_RELEASE',
    UpdateHuaweiPhasedRelease = 'UPDATE_HUAWEI_PHASED_RELEASE',
    UpdateExpoEASRollout = 'UPDATE_EXPO_EAS_ROLLOUT',

    PauseApplePhasedRelease = 'PAUSE_APPLE_PHASED_RELEASE',
    PauseGooglePhasedRelease = 'PAUSE_GOOGLE_PHASED_RELEASE',
    PauseHuaweiPhasedRelease = 'PAUSE_HUAWEI_PHASED_RELEASE',

    ResumeApplePhasedRelease = 'RESUME_APPLE_PHASED_RELEASE',
    StartAutomatedAndroidStagedRollout = 'START_AUTOMATED_ANDROID_STAGED_ROLLOUT',

    CompleteApplePhasedRelease = 'COMPLETE_APPLE_PHASED_RELEASE',
    CompleteGooglePhasedRelease = 'COMPLETE_GOOGLE_PHASED_RELEASE',
    CompleteHuaweiPhasedRelease = 'COMPLETE_HUAWEI_PHASED_RELEASE',

    TagRelease = 'TAG_RELEASE',

    // Metadata
    UpdateMetadata = 'UPDATE_METADATA',
    ApproveMetadata = 'APPROVE_METADATA',

    // Screenshots
    DeleteScreenshot = 'DELETE_SCREENSHOT',
    DeleteAppPreview = 'DELETE_APP_PREVIEW',
    ReorderScreenshots = 'REORDER_SCREENSHOTS',
    UpdateScreenshotsApproval = 'UPDATE_SCREENSHOTS_APPROVAL',

    // Approval override
    UpdateApprovalOverride = 'UPDATE_APPROVAL_OVERRIDE',

    // Submission items
    AddReviewSubmissionItem = 'ADD_REVIEW_SUBMISSION_ITEM',
    RemoveReviewSubmissionItem = 'REMOVE_REVIEW_SUBMISSION_ITEM',
    UnblockReviewSubmission = 'UNBLOCK_REVIEW_SUBMISSION',
    CancelReviewSubmission = 'CANCEL_REVIEW_SUBMISSION',

    // Build System
    TriggerBuildSystemWorkflow = 'TRIGGER_BUILD_SYSTEM_WORKFLOW',
    RetryArtifactUpload = 'RETRY_ARTIFACT_UPLOAD',
    RetryDsymUpload = 'RETRY_DSYM_UPLOAD',
    LockBuild = 'LOCK_BUILD',
    UnlockBuild = 'UNLOCK_BUILD',
    OverrideCIFailure = 'OVERRIDE_CI_FAILURE',

    // Regression Testing
    UpdateRegressionStatus = 'UPDATE_REGRESSION_STATUS',

    // Rollbacks
    StartResignSequence = 'START_RESIGN_SEQUENCE',
}

export interface EndBetaSoakRequest {
    userId: string;
}

export interface StartResignSequenceForStepRunRequest {
    signingKeyId?: string;
}

export interface AssignBetaGroupsToBuildRequest {
    buildId: string;
    groupIds: string[];
}

export interface CreateOrUpdateBuildLocalizationsRequest {
    buildId: string;
    localizationId?: string;
    localizations: {
        notes: string;
        locale: string;
    }[];
}

export interface SubmitBetaBuildToReviewRequest {
    buildId: string;
}

export interface AddIndividualTesterToBuildRequest {
    buildId: string;
    testerId: string;
}

export interface PromoteBuildsToTestingTrackRequest {
    buildIds: string[];
    track: string;
}

export interface UpdateSelectedBuildsInAppStoreRequest {
    buildIds: string[];
    // Builds retained from previous releases, saved from the same dialog. Omit to leave them untouched
    retainedBuildIds?: string[];
}

export interface SubmitAppForReviewRequest {
    releasePreviousSubmissionToAllUsersFirst: boolean;
    releaseBetaTrackToFullPercentagePriorToSubmission?: boolean;
    shouldMatchPreviousPhasedRelease: boolean;
}

export interface ReleaseAppleVersionRequest {
    releasePreviousSubmissionToAllUsersFirst: boolean;
}

export interface UpdateAppleAppStoreSettingsRequest {
    releaseType: AppleAppStoreReleaseType;
    earliestReleaseDate?: string;
    phasedReleaseEnabled: boolean;
}

export interface UpdateGoogleAppStoreSettingsRequest extends UpdatePlayAppStoreReleaseDefaults {
    inAppUpdatePriority?: number;
}

export interface UpdateAppStoreSettingsRequest {
    // ASC
    releaseType?: AppleAppStoreReleaseType;
    earliestReleaseDate?: string;
    phasedReleaseEnabled?: boolean;

    // GPlay + Huawei
    stagedRelease?: boolean;
    initialStagedRolloutPercentage?: number;
    inAppUpdatePriority?: number;
}

export interface UpdateReviewDetailsRequest {
    id: string;
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactPhone: string;
    notes?: string;
    demoAccountName?: string;
    demoAccountPassword?: string;
    demoAccountRequired: boolean;
}

export interface UpdatePhasedReleaseRequestBase {
    resumeAutomations?: AutomationType[];
    actionType?: RolloutAction;
}

export interface UpdateExpoEASRolloutRequest extends UpdatePhasedReleaseRequestBase {
    targetPercentage: number;
}

export interface UpdateAndroidPhasedReleaseRequest extends UpdatePhasedReleaseRequestBase {
    userFraction: string | number;
    phaseState: GooglePlayStoreState;
}

export interface UpdateApplePhasedReleaseRequest extends UpdatePhasedReleaseRequestBase {
    userFraction: void; // Not needed for Apple
    phaseState: AppleAppStorePhasedReleaseState;
}

export interface UpdateHuaweiPhasedReleaseRequest extends UpdatePhasedReleaseRequestBase {
    userFraction: string | number;
    phaseState: HuaweiPhasedReleaseState;
}

export type UpdatePhasedReleaseRequest =
    | UpdateAndroidPhasedReleaseRequest
    | UpdateApplePhasedReleaseRequest
    | UpdateHuaweiPhasedReleaseRequest;

export interface TagReleaseRequest {
    commitHash: string;
    tagName?: string;
}

export interface CreateChecklistItemRequest {
    title: string;
    notifyEnabled: boolean;
    blocksEvents?: ChecklistItemBlockableEvent[];
    type: ChecklistItemType;
    placement: ChecklistItemPlacement;
    description?: string;
    approverGroups: UserGroupId[];
    owners: string[];
    targetCompletion?: RelativeTimeTrigger;
    incompleteReminderEnabled: boolean;
    autocompleteAtTargetCompletionEnabled: boolean;
    includeDescriptionInNotificationsEnabled: boolean;
    disableReminderAfterReleaseComplete: boolean;
    channels?: string[];
    attachments?: ChecklistAttachment[];
}

export type UpdateChecklistItemRequest = CreateChecklistItemRequest;

export type DeleteChecklistItemRequest = void;

export interface UpdateChecklistItemCommentRequest {
    comment: string;
    attachments?: ChecklistAttachment[];
}

export interface UpdateChecklistItemStatusRequest {
    status?: ChecklistItemStatusType;
}

export interface CreateOrgChecklistItemTemplateRequest extends CreateChecklistItemRequest {
    orgId: string;
    shouldApplyToAllAppsAndPlatforms: boolean;
    apps: string[];
    platforms: AppPlatform[];
}

export interface UpdateOrgChecklistItemTemplateRequest extends CreateChecklistItemRequest {
    checklistItemId: string;
    shouldApplyToAllAppsAndPlatforms: boolean;
    apps: string[];
    platforms: AppPlatform[];
}

export interface DeleteOrgChecklistItemTemplateRequest {
    checklistItemId: string;
}

export const UPDATE_STEP_RUNS = 'UPDATE_STEP_RUNS';

export interface TryUpdateStepRunsAction {
    type: typeof UPDATE_STEP_RUNS;
    payload: RunwayModelsResponse['stepRuns'];
}

export const updateStepRuns = (payload: RunwayModelsResponse['stepRuns']): TryUpdateStepRunsAction => {
    return {
        type: UPDATE_STEP_RUNS,
        payload: payload,
    };
};

export interface UpdateMetadataRequest {
    localizations: LocalizationUpdateRequest[];
}

export interface ApproveMetadataRequest {
    isApproved: boolean;
    userId: string;
}

export interface DeleteScreenshotRequest {
    screenshotId: string;
    // Android-specific
    language?: string;
    imageType?: string;
}

export interface DeleteAppPreviewStepRunRequest {
    previewId: string;
    language?: string;
    imageType?: string;
}

export interface ReorderScreenshotsRequest {
    screenshotSetId: string;
    orderedScreenshotIds: string[];
}

export interface UpdateScreenshotsApprovalRequest {
    isApproved: boolean;
}

export interface UpdateApprovalOverrideRequest {
    requiresManualApproval: boolean;
}

export interface AddReviewSubmissionItemRequest {
    itemId: string;
    type: AppStoreReviewSubmissionItemType;
}

export interface RemoveReviewSubmissionItemRequest {
    itemId: string;
}

export interface TriggerBuildSystemWorkflowRequest {
    workflowType: WorkflowType;
    additionalWorkflowVariables?: Record<string, string>;
    stopInProgressBuilds?: boolean;
}

export interface RetryArtifactUploadRequest {
    workflowRunId: string;
    artifactId: string;
    destinationInstallationId: string;
    trackName: Nullable<string>;
}

export interface RetryDsymUploadStepRunRequest {
    workflowRunId: string;
    artifactId: string;
    stabilityMonitoringInstallationId?: string;
}

export interface LockBuildRequest {
    buildHash: string;
}

export interface OverrideCIFailureRequest {
    isApproved: boolean;
    userId: string;
}

export interface UpdateRegressionStatusRequest {
    status: RegressionStatus;
    buildHash?: string;
    userId: string;
}
